import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Upload, CheckCircle2, Clock, XCircle } from "lucide-react";

const DOC_TYPES = [
    { key: "passport", label: "Passport / Government ID" },
    { key: "company_registration", label: "Company Registration (CIN/MOA)" },
    { key: "pan_card", label: "PAN / Tax ID" },
];

export default function Kyc() {
    const { user, refresh } = useAuth();
    const [status, setStatus] = useState({ status: "pending", submissions: [] });
    const [docType, setDocType] = useState("passport");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/kyc/status");
            setStatus(data);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file) return toast.error("Attach a document first");
        if (file.size > 3_500_000) return toast.error("File too large (max 3MB)");
        setBusy(true);
        try {
            const dataUrl = await new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(r.result);
                r.onerror = rej;
                r.readAsDataURL(file);
            });
            await api.post("/kyc/submit", {
                doc_type: docType,
                doc_name: file.name,
                doc_data_url: dataUrl,
            });
            toast.success("Submitted for review. We'll email you within 24 hours.");
            setFile(null);
            await load();
            await refresh?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const statusPill = (s) => {
        const map = {
            verified: { Icon: CheckCircle2, cls: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5", label: "Verified" },
            under_review: { Icon: Clock, cls: "text-silver-300 border-silver-300/30 bg-silver-300/5", label: "Under review" },
            rejected: { Icon: XCircle, cls: "text-red-300 border-red-300/30 bg-red-300/5", label: "Rejected" },
            pending: { Icon: ShieldCheck, cls: "text-silver-400 border-silver-400/20", label: "Not started" },
        };
        const v = map[s] || map.pending;
        return (
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-full text-xs tracking-widest uppercase ${v.cls}`} data-testid="kyc-status-pill">
                <v.Icon className="w-3.5 h-3.5" /> {v.label}
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1100px] mx-auto px-4 md:px-10 pt-14 pb-28">
                <div className="flex items-center gap-3 mb-3 text-[11px] tracking-[0.3em] text-silver-400 uppercase">
                    <ShieldCheck className="w-4 h-4" /> Identity · Verification
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-6">
                    Verify once. <span className="silver-text italic">Transact forever.</span>
                </h1>
                <p className="max-w-2xl text-silver-400 text-base mb-10">
                    Silver Screen Xchange only allows verified counterparties on deal rooms above $10k.
                    Upload a government ID and, for companies, proof of registration. Review time is under 24 hours.
                </p>

                <div className="mb-8 flex items-center gap-4">
                    <span className="text-xs tracking-[0.3em] uppercase text-silver-400">Your status</span>
                    {statusPill(status.status)}
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <form onSubmit={onSubmit} className="bg-ink-200/60 border border-white/10 rounded-sm p-6" data-testid="kyc-form">
                        <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-5">Submit a document</div>
                        <label className="block text-[11px] tracking-widest uppercase text-silver-400 mb-2">Document type</label>
                        <select
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                            className="w-full bg-ink-300 border border-white/10 px-3 py-2.5 text-sm mb-5"
                            data-testid="kyc-doctype"
                        >
                            {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                        </select>
                        <label className="block text-[11px] tracking-widest uppercase text-silver-400 mb-2">File (PDF / JPG / PNG, max 3MB)</label>
                        <label className="flex flex-col items-center justify-center gap-3 border border-dashed border-white/15 rounded-sm py-8 cursor-pointer hover:border-platinum/40 transition-colors">
                            <Upload className="w-5 h-5 text-silver-400" />
                            <span className="text-sm text-silver-300">{file?.name || "Click to choose a file"}</span>
                            <input
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="hidden"
                                data-testid="kyc-file-input"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={busy}
                            className="mt-6 w-full bg-platinum text-ink-400 px-4 py-3 text-sm tracking-widest uppercase hover:bg-white transition-colors disabled:opacity-50"
                            data-testid="kyc-submit"
                        >
                            {busy ? "Uploading…" : "Submit for review"}
                        </button>
                    </form>

                    <div className="bg-ink-200/60 border border-white/10 rounded-sm p-6">
                        <div className="text-xs tracking-[0.3em] uppercase text-silver-400 mb-5">History</div>
                        {status.submissions.length === 0 ? (
                            <div className="text-silver-500 text-sm">No submissions yet.</div>
                        ) : (
                            <div className="space-y-3" data-testid="kyc-history">
                                {status.submissions.map((s) => (
                                    <div key={s.id} className="flex items-center justify-between border-b border-white/5 pb-3">
                                        <div>
                                            <div className="text-sm text-silver-200">{s.doc_name}</div>
                                            <div className="text-[11px] tracking-widest uppercase text-silver-500">
                                                {s.doc_type.replace("_", " ")} · {new Date(s.submitted_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                        {statusPill(s.status)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
